<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import GgSupportSelect from './GgSupportSelect.vue';

const props = defineProps({
  action: { type: String, required: true },
  snapshot: { type: Object, required: true },
  error: { type: String, default: '' },
});
const emit = defineEmits(['confirm', 'close']);
const { t } = useI18n();
const dialog = ref(null);
const reasonId = ref('');
const pauseNote = ref('');
const reason = computed(() =>
  props.snapshot.pause_reasons.find(item => String(item.id) === reasonId.value)
);
const canConfirm = computed(() => {
  if (props.action === 'pause')
    return (
      Boolean(reason.value) &&
      pauseNote.value.length <= 400 &&
      !pauseNote.value.includes('\0')
    );
  return true;
});
const confirm = () => {
  if (!canConfirm.value) return;
  let details = {};
  if (props.action === 'pause')
    details = {
      pause_reason_id: Number(reasonId.value),
      ...(pauseNote.value.trim() ? { pause_note: pauseNote.value.trim() } : {}),
    };
  emit('confirm', details);
};
onMounted(() => dialog.value.open());
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t(`GG_SUPPORT_STATE.ACTIONS.${action}`)"
    :description="
      t('GG_SUPPORT_STATE.CONFIRM_CONTEXT', {
        code: snapshot.ticket.ticket_code,
        state: snapshot.ticket.state_label,
      })
    "
    :confirm-button-label="t('GG_SUPPORT_STATE.CONFIRM')"
    :disable-confirm-button="!canConfirm"
    overflow-y-auto
    @confirm="confirm"
    @close="emit('close')"
  >
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ error }}
    </p>
    <div v-if="action === 'pause'" class="flex flex-col gap-3">
      <GgSupportSelect
        id="gg-support-pause-reason"
        v-model="reasonId"
        :label="t('GG_SUPPORT_STATE.PAUSE_REASON')"
        :options="
          snapshot.pause_reasons.map(item => ({
            value: String(item.id),
            label: item.name,
          }))
        "
      />
      <p v-if="reason" class="m-0 text-sm text-n-slate-11">
        {{
          t('GG_SUPPORT_STATE.PAUSE_EFFECT', {
            state: reason.target_status,
            sla: t(
              reason.pauses_sla
                ? 'GG_SUPPORT_STATE.SLA_PAUSES'
                : 'GG_SUPPORT_STATE.SLA_CONTINUES'
            ),
          })
        }}
      </p>
      <TextArea
        id="gg-support-pause-note"
        v-model="pauseNote"
        :label="t('GG_SUPPORT_STATE.PAUSE_NOTE')"
        :max-length="400"
        show-character-count
        custom-text-area-class="!h-24"
      />
    </div>
    <p v-else class="m-0 text-sm text-n-slate-11">
      {{
        t(
          action === 'resume'
            ? 'GG_SUPPORT_STATE.RESUME_HINT'
            : 'GG_SUPPORT_STATE.CHANGE_HINT'
        )
      }}
    </p>
  </Dialog>
</template>
